import {
  useEffect,
  useLayoutEffect,
  useRef,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from "react";

// SSR-safe layout effect.
const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;


/**
 * Framer Motion-style reveal primitives — CSS-only, no runtime deps.
 *
 * Animation spec (matches the rest of the app):
 *   - initial:  opacity 0, translateY(24px)
 *   - animate:  opacity 1, translateY(0)
 *   - duration: 0.7s
 *   - easing:   cubic-bezier(0.22, 1, 0.36, 1)
 *   - stagger:  0.08s between children of <AnimatedCardGroup>
 *   - respects `prefers-reduced-motion`
 *
 * Usage:
 *
 *   // Single card, reveals when it scrolls into view
 *   <AnimatedCard className="rounded-3xl bg-card p-6">…</AnimatedCard>
 *
 *   // Group of cards, staggered on mount (no scroll observer)
 *   <AnimatedCardGroup as="ul" className="grid gap-4 md:grid-cols-3">
 *     <AnimatedCard as="li">…</AnimatedCard>
 *     <AnimatedCard as="li">…</AnimatedCard>
 *     <AnimatedCard as="li">…</AnimatedCard>
 *   </AnimatedCardGroup>
 *
 *   // Group of cards, staggered when the group scrolls into view
 *   <AnimatedCardGroup revealOnScroll className="grid gap-4 md:grid-cols-3">
 *     {items.map((it) => <AnimatedCard key={it.id}>…</AnimatedCard>)}
 *   </AnimatedCardGroup>
 */

const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const DURATION_MS = 700;
const STAGGER_MS = 80;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

type CommonProps = {
  as?: ElementType;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

/**
 * Reveal a single element with the standard 0.7s opacity/y transition.
 * - Default: reveals when it enters the viewport (IntersectionObserver).
 * - Set `revealOnMount` to skip the observer (e.g. inside a tab pane that
 *   is already visible when mounted, or inside an <AnimatedCardGroup>).
 * - `delayMs` adds a one-off delay (e.g. for manual stagger).
 */
export function AnimatedCard({
  as: Tag = "div",
  className = "",
  style,
  children,
  revealOnMount = false,
  delayMs = 0,
}: CommonProps & { revealOnMount?: boolean; delayMs?: number }) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced = prefersReducedMotion();
    const show = () => {
      if (reduced) {
        el.style.transition = "none";
      }
      el.style.opacity = "1";
      el.style.transform = "translate3d(0, 0, 0)";
    };

    if (revealOnMount || typeof IntersectionObserver === "undefined") {
      // Run on next frame so the initial styles paint first and the
      // transition actually animates.
      const id = window.requestAnimationFrame(show);
      return () => window.cancelAnimationFrame(id);
    }

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            show();
            obs.unobserve(e.target);
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [revealOnMount]);

  const Component = Tag as ElementType;
  const baseStyle: CSSProperties = {
    opacity: 0,
    transform: "translate3d(0, 24px, 0)",
    transition: `opacity ${DURATION_MS}ms ${EASE}, transform ${DURATION_MS}ms ${EASE}`,
    transitionDelay: delayMs > 0 ? `${delayMs}ms` : undefined,
    willChange: "opacity, transform",
    ...style,
  };

  return (
    <Component
      ref={ref as never}
      className={className}
      style={baseStyle}
      data-animated-card=""
    >
      {children}
    </Component>
  );
}

/**
 * Wraps a group of <AnimatedCard> children and applies a 0.08s stagger.
 * - Default: each child reveals on mount, staggered (good when the group
 *   is already on screen, e.g. inside a tab or a section above the fold).
 * - `revealOnScroll`: waits for the group itself to enter the viewport,
 *   then staggers the children.
 */
export function AnimatedCardGroup({
  as: Tag = "div",
  className = "",
  style,
  children,
  staggerMs = STAGGER_MS,
  revealOnScroll = false,
  animateAllChildren = false,
}: CommonProps & {
  staggerMs?: number;
  revealOnScroll?: boolean;
  /**
   * When true, animate every direct child element (not only those rendered
   * via <AnimatedCard>). Lets you drop the group around an existing card
   * grid without rewriting each child.
   */
  animateAllChildren?: boolean;
}) {
  const ref = useRef<HTMLElement | null>(null);

  const selector = animateAllChildren
    ? ":scope > *"
    : ":scope > [data-animated-card]";

  // Set initial (hidden) styles synchronously so the first paint already
  // shows the elements offset + transparent — avoids a flash when we add
  // the group around children that don't use <AnimatedCard>.
  useIsoLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    if (!animateAllChildren) return; // <AnimatedCard> sets its own initial style
    const cards = Array.from(root.querySelectorAll<HTMLElement>(selector));
    cards.forEach((el) => {
      el.style.opacity = "0";
      el.style.transform = "translate3d(0, 24px, 0)";
      el.style.transition = `opacity ${DURATION_MS}ms ${EASE}, transform ${DURATION_MS}ms ${EASE}`;
      el.style.willChange = "opacity, transform";
    });
  }, [animateAllChildren, selector, children]);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const cards = Array.from(root.querySelectorAll<HTMLElement>(selector));
    if (!cards.length) return;

    const reduced = prefersReducedMotion();
    const reveal = () => {
      cards.forEach((el, i) => {
        if (reduced) {
          el.style.transition = "none";
          el.style.transitionDelay = "0ms";
        } else {
          el.style.transitionDelay = `${i * staggerMs}ms`;
        }
        window.requestAnimationFrame(() => {
          el.style.opacity = "1";
          el.style.transform = "translate3d(0, 0, 0)";
        });
      });
    };

    if (!revealOnScroll || typeof IntersectionObserver === "undefined") {
      const id = window.requestAnimationFrame(reveal);
      return () => window.cancelAnimationFrame(id);
    }

    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            reveal();
            obs.unobserve(e.target);
            break;
          }
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 },
    );
    obs.observe(root);
    return () => obs.disconnect();
  }, [staggerMs, revealOnScroll, children, selector]);

  const Component = Tag as ElementType;
  return (
    <Component ref={ref as never} className={className} style={style}>
      {children}
    </Component>
  );
}

export default AnimatedCard;

